import React, { useState } from "react";
import { pick } from "lodash";
import {
  ASSESSMENT_SUPERCATEGORY,
  ASSESSMENT_SUPERCATEGORY_TYPES,
  ASSESSMENT_TYPE_TO_TITLE,
} from "@sscs/report/constants";
import { RiskSuperCategory, SDLCMetadata } from "@sscs/types";
import { Card, Collapse, ExpandButton } from "@sscs/ui";
import { getClassName } from "@sscs/utils";
import { EntitlementIcon } from "Components";
import { GetOrganizationUsageResponse } from "Types";
import styles from "./entitlements.module.scss";

type AssessmentCategoriesProps = {
  assessmentEntitlements: Record<SDLCMetadata.assessment_type_t, boolean>;
};

const assessmentCategories: SDLCMetadata.assessment_type_t[] = [
  "licenses",
  "vulnerabilities",
  "tampering",
  "secrets",
  "hardening",
  "malware",
];

const EntitlementCategory = ({
  category,
  isActive,
}: {
  category: SDLCMetadata.assessment_type_t;
  isActive: boolean;
}) => (
  <div
    className={
      isActive ? styles["risk-item-active"] : styles["risk-item-disabled"]
    }
  >
    <span className={styles["risk-icon"]}>
      <EntitlementIcon fontSize="large" enabled={isActive} />
    </span>
    <div className={styles["risk-label"]}>
      <span className={styles["risk-title"]}>
        {ASSESSMENT_TYPE_TO_TITLE[category]}
      </span>
      <span className={styles["risk-subline"]}>
        {isActive ? "Enabled" : "Disabled"}
      </span>
    </div>
  </div>
);

const AssessmentCategories = ({
  assessmentEntitlements,
}: AssessmentCategoriesProps) => (
  <div className={styles["grid-container"]}>
    {Object.keys(ASSESSMENT_SUPERCATEGORY_TYPES).map(
      (superCategory: RiskSuperCategory) => {
        const categories = ASSESSMENT_SUPERCATEGORY_TYPES[superCategory];

        return (
          <div key={superCategory} className={styles["column"]}>
            <span
              className={getClassName(styles["risk-category-title"], {
                [styles["risk-category-title-disabled"]]: categories.every(
                  (category) => !assessmentEntitlements[category],
                ),
              })}
            >
              {ASSESSMENT_SUPERCATEGORY[superCategory]}
            </span>
            {categories.map((category) => (
              <EntitlementCategory
                key={category}
                category={category}
                isActive={assessmentEntitlements[category]}
              />
            ))}
          </div>
        );
      },
    )}
  </div>
);

type SummaryProps = {
  assessmentEntitlements: Record<SDLCMetadata.assessment_type_t, boolean>;
};

const Summary = ({ assessmentEntitlements }: SummaryProps) => {
  const allEntitlementsValues = Object.values(assessmentEntitlements);
  const enabledEntitlementsCount = allEntitlementsValues.filter(Boolean).length;

  const areAllEntitlementsEnabled =
    enabledEntitlementsCount === allEntitlementsValues.length;

  return (
    <>
      <span className={styles["summary-icon"]}>
        <EntitlementIcon
          fontSize="large"
          enabled={areAllEntitlementsEnabled}
          color={areAllEntitlementsEnabled ? undefined : "warning"}
        />
      </span>

      <span>
        <strong>
          {areAllEntitlementsEnabled
            ? "All"
            : `${enabledEntitlementsCount} out of ${allEntitlementsValues.length}`}
        </strong>{" "}
        SAFE assessment entitlements{" "}
        {enabledEntitlementsCount === 1 ? "is" : "are"} currently{" "}
        <strong>enabled</strong>.{" "}
        {areAllEntitlementsEnabled
          ? "Data from each risk category is included in the report and can be accessed."
          : "To access additional SAFE assessment categories, please upgrade to the Spectra Assure Enterprise+ tier."}
      </span>
    </>
  );
};

type EntitlementsProps = {
  data?: GetOrganizationUsageResponse["entitlements"];
};

const Entitlements = ({
  data,
}: EntitlementsProps): React.JSX.Element | null => {
  const [isExpanded, setIsExpanded] = useState(false);
  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  if (!data) return null;

  const assessmentEntitlements = pick(data, assessmentCategories);

  return (
    <Card.Card>
      <div
        className={getClassName(styles.header, {
          [styles["header-expanded"]]: isExpanded,
        })}
      >
        <ExpandButton
          id="entitlements-expand"
          className={styles["expand-toggle"]}
          isExpanded={isExpanded}
          onClick={handleExpandClick}
          aria-label={`${
            isExpanded ? "Hide" : "Show"
          } SAFE assessment entitlements`}
        />
        <Summary assessmentEntitlements={assessmentEntitlements} />
      </div>

      <Collapse
        transitionIn={isExpanded}
        className={styles["collapse-content"]}
      >
        <AssessmentCategories assessmentEntitlements={assessmentEntitlements} />
      </Collapse>
    </Card.Card>
  );
};

export default Entitlements;
